import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    ArrowLeft, Handshake, Loader2, Download, ChevronLeft, ChevronRight,
    ChevronDown, Plus, Copy, Check, BanknoteArrowUp, TriangleAlert, Mail,
} from 'lucide-react';
import {
    listPartnerOverview,
    listPartners,
    provisionPartner,
    updatePartner,
    listPartnerReferrals,
    markPartnerPaid,
    referralLink,
    formatPence,
    periodKey,
    toCsv,
    type PartnerOverviewRow,
    type PartnerRecord,
    type PartnerReferralRow,
} from '../../lib/partners';

/**
 * Partner programme — management surface.
 *
 * Deliberately not a partner portal. Partners are not ForgeDash users and have
 * no auth.users row; at the volumes this programme is designed for (see
 * docs/decisions/partner-programme.md §9.3) the workflow is: read this page,
 * export the CSV, bank transfer, press Mark paid.
 *
 * One card per partner at every width rather than the usual cards-on-mobile /
 * table-on-desktop split. Each row carries actions, an editable rate and an
 * expandable drill-down; maintaining that in two parallel markup trees would
 * double the bug surface for an internal page that lists ~20 rows. The CSV
 * export covers the scan-everything-at-once need a table would have served.
 */

const MIN_PAYOUT_PENCE = 5000;   // terms §5

function shiftMonth(period: string, delta: number): string {
    const [y, m] = period.split('-').map(Number);
    return periodKey(new Date(Date.UTC(y, m - 1 + delta, 1)));
}

function monthLabel(period: string): string {
    const [y, m] = period.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 1))
        .toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

const STATUS_CLS: Record<PartnerRecord['status'], string> = {
    active: 'text-success border-success/30',
    paused: 'text-gold border-gold/30',
    terminated: 'text-fg-4 border-line-glow/30',
};

/** Same shape the `partners.code` CHECK enforces, so a bad code fails locally. */
const CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/;

const pct = (bps: number) => `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 1)}%`;

const inputCls =
    'w-full min-w-0 rounded border border-danger/30 bg-scrim/40 px-3 py-2 text-sm text-fg outline-none focus:border-danger/50';
const btnCls =
    'flex h-11 items-center justify-center gap-2 rounded border border-danger/20 px-4 text-xs uppercase tracking-widest text-danger transition-colors hover:bg-danger/10 disabled:opacity-30';

// ─── Add partner ────────────────────────────────────────────────────────────

function AddPartnerForm({ onCreated }: { onCreated: () => void }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [initialPct, setInitialPct] = useState('30');
    const [tailPct, setTailPct] = useState('15');
    const [termMonths, setTermMonths] = useState('12');

    const normalisedCode = code.trim().toUpperCase();
    const codeValid = CODE_PATTERN.test(normalisedCode);

    const submit = async () => {
        if (!codeValid) return toast.error('Code must be 3–32 chars: A–Z, 0–9, dash or underscore');
        if (name.trim().length < 2) return toast.error('Name is required');
        // Required, not optional. contact_email is what enforces BOTH the
        // self-referral exclusion and the reciprocal-referral guard (it is how
        // partners.org_id gets filled in). A partner without one has both
        // defences silently switched off.
        if (!email.trim()) return toast.error('Contact email is required — it enforces the self-referral and reciprocal exclusions');
        setSaving(true);
        try {
            // Creates the Stripe promotion code and the partners row together.
            // Half a partner is the worst state this programme has: the Stripe
            // code alone gives the customer their discount while commission
            // silently never accrues.
            const result = await provisionPartner({
                code: normalisedCode,
                display_name: name.trim(),
                contact_email: email.trim(),
                rate_bps_initial: Math.round(parseFloat(initialPct) * 100),
                rate_bps_tail: Math.round(parseFloat(tailPct) * 100),
                initial_term_months: parseInt(termMonths, 10),
            });
            toast.success(
                result.partner.org_id
                    ? `${normalisedCode} created, Stripe code live, linked to their ForgeDash account`
                    : `${normalisedCode} created and the Stripe code is live`,
            );
            setCode(''); setName(''); setEmail('');
            setInitialPct('30'); setTailPct('15'); setTermMonths('12');
            setOpen(false);
            onCreated();
        } catch (e) {
            toast.error((e as Error).message);
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <button onClick={() => setOpen(true)} className={btnCls}>
                <Plus className="h-3 w-3" /> Add partner
            </button>
        );
    }

    return (
        <section className="min-w-0 rounded-xl border border-danger/20 bg-tint/[0.02] p-4 sm:p-6">
            <h2 className="mb-5 font-mono text-sm uppercase tracking-widest text-danger/70">New partner</h2>
            <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="min-w-0">
                        <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">Code</label>
                        <input
                            className={inputCls}
                            value={code}
                            onChange={e => setCode(e.target.value)}
                            placeholder="DAVE50"
                            autoCapitalize="characters"
                        />
                        {code.trim() !== '' && !codeValid && (
                            <p className="mt-1 text-xs text-gold">3–32 characters: A–Z, 0–9, dash or underscore.</p>
                        )}
                    </div>
                    <div className="min-w-0">
                        <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">Name</label>
                        <input className={inputCls} value={name} onChange={e => setName(e.target.value)}
                            placeholder="Dave the Mobile Mechanic" />
                    </div>
                </div>
                <div className="min-w-0">
                    <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">
                        Contact email <span className="text-danger/70">*</span>
                    </label>
                    <input className={inputCls} type="email" value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="dave@example.com" required aria-required="true" />
                    <p className="mt-1 text-xs text-fg-4">
                        Required. Blocks self-referral, and links this partner to their own
                        ForgeDash account so they cannot swap referrals with another partner.
                    </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                    <div className="min-w-0">
                        <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">Initial rate %</label>
                        <input className={inputCls} type="number" min="0" max="100" step="0.5"
                            value={initialPct} onChange={e => setInitialPct(e.target.value)} />
                    </div>
                    <div className="min-w-0">
                        <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">Tail rate %</label>
                        <input className={inputCls} type="number" min="0" max="100" step="0.5"
                            value={tailPct} onChange={e => setTailPct(e.target.value)} />
                    </div>
                    <div className="min-w-0">
                        <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-4">Initial term (months)</label>
                        <input className={inputCls} type="number" min="0" max="600" step="1"
                            value={termMonths} onChange={e => setTermMonths(e.target.value)} />
                    </div>
                </div>

                {codeValid && (
                    <p className="break-all rounded border border-danger/20 bg-scrim/30 px-3 py-2 font-mono text-xs text-fg-3">
                        {referralLink(normalisedCode)}
                    </p>
                )}
                <p className="text-xs leading-relaxed text-fg-4">
                    The code must also exist as a <strong className="text-fg-3">Stripe promotion code</strong>,
                    spelled identically. Nothing here can check that, and a mismatch means the discount works
                    while the commission silently never accrues.
                </p>

                <div className="flex flex-wrap gap-3">
                    <button onClick={submit} disabled={saving} className={btnCls}>
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Create
                    </button>
                    <button onClick={() => setOpen(false)} className={btnCls}>Cancel</button>
                </div>
            </div>
        </section>
    );
}

// ─── One partner ────────────────────────────────────────────────────────────

function PartnerCard({
    row, record, period, onChanged,
}: {
    row: PartnerOverviewRow;
    record: PartnerRecord | undefined;
    period: string;
    onChanged: () => void;
}) {
    const [expanded, setExpanded] = useState(false);
    const [referrals, setReferrals] = useState<PartnerReferralRow[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [editingRates, setEditingRates] = useState(false);
    const [initialPct, setInitialPct] = useState('');
    const [tailPct, setTailPct] = useState('');
    const [editingEmail, setEditingEmail] = useState(false);
    const [emailDraft, setEmailDraft] = useState('');

    const outstanding = Number(row.outstanding_pence);
    const dueNow = outstanding >= MIN_PAYOUT_PENCE;
    const status = record?.status ?? row.status;

    // A partner with no contact_email has NO self-referral exclusion: the check
    // in fn_provision_org compares the signup's email against this column, and a
    // NULL there means the comparison is skipped and attribution goes ahead. So
    // this is not a missing address, it is a disarmed guardrail -- surfaced on
    // the collapsed card rather than hidden behind the drill-down.
    const contactEmail = record?.contact_email ?? null;
    const exclusionInert = !!record && !contactEmail && status !== 'terminated';

    const toggle = async () => {
        const next = !expanded;
        setExpanded(next);
        if (next && referrals === null) {
            try {
                setReferrals(await listPartnerReferrals(row.partner_id));
            } catch (e) {
                toast.error((e as Error).message);
                setReferrals([]);
            }
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(referralLink(row.code));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error('Could not copy — select the link and copy manually');
        }
    };

    const setStatus = async (next: PartnerRecord['status']) => {
        if (next === 'terminated' &&
            !window.confirm(`Terminate ${row.display_name}? They stop accruing commission on new payments. Existing unpaid commission is unaffected.`)) return;
        setBusy(true);
        try {
            await updatePartner(row.partner_id, { status: next });
            toast.success(`${row.code} ${next}`);
            onChanged();
        } catch (e) {
            toast.error((e as Error).message);
        } finally {
            setBusy(false);
        }
    };

    const beginEditRates = () => {
        setInitialPct(String((record?.rate_bps_initial ?? 3000) / 100));
        setTailPct(String((record?.rate_bps_tail ?? 1500) / 100));
        setEditingRates(true);
    };

    const saveRates = async () => {
        const initial = Math.round(parseFloat(initialPct) * 100);
        const tail = Math.round(parseFloat(tailPct) * 100);
        if (!Number.isFinite(initial) || !Number.isFinite(tail) ||
            initial < 0 || initial > 10000 || tail < 0 || tail > 10000) {
            return toast.error('Rates must be between 0 and 100%');
        }
        // Rates decide what a real person is owed, and the change applies to
        // every future accrual for every customer they have ever referred.
        if (!window.confirm(
            `Change ${row.display_name} to ${pct(initial)} for the first ` +
            `${record?.initial_term_months ?? 12} months, then ${pct(tail)}?\n\n` +
            `This applies to all FUTURE accruals, including for customers already referred. ` +
            `Commission already accrued is not changed.`
        )) return;
        setBusy(true);
        try {
            await updatePartner(row.partner_id, { rate_bps_initial: initial, rate_bps_tail: tail });
            toast.success('Rates updated');
            setEditingRates(false);
            onChanged();
        } catch (e) {
            toast.error((e as Error).message);
        } finally {
            setBusy(false);
        }
    };

    const beginEditEmail = () => {
        setEmailDraft(contactEmail ?? '');
        setEditingEmail(true);
    };

    const saveEmail = async () => {
        const next = emailDraft.trim().toLowerCase();
        // Deliberately permissive: this is one admin typing a known address, and
        // a regex that rejects a valid-but-unusual address is worse here than
        // one that accepts a typo -- a typo only means the exclusion does not
        // fire for that partner, which is the state we are already in.
        if (next && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
            return toast.error('That does not look like an email address');
        }
        setBusy(true);
        try {
            await updatePartner(row.partner_id, { contact_email: next || null });
            toast.success(next ? 'Contact email saved' : 'Contact email cleared');
            setEditingEmail(false);
            onChanged();
        } catch (e) {
            toast.error((e as Error).message);
        } finally {
            setBusy(false);
        }
    };

    const markPaid = async () => {
        if (!window.confirm(
            `Record a payout of ${formatPence(outstanding)} to ${row.display_name}?\n\n` +
            `Do this AFTER the bank transfer has gone out. It marks every commission ` +
            `that has cleared its 30-day hold as paid.`
        )) return;
        setBusy(true);
        try {
            const res = await markPartnerPaid(row.partner_id);
            if (res.marked_count === 0) {
                toast('Nothing was due — everything is still within its 30-day hold.');
            } else {
                toast.success(`Marked ${res.marked_count} commission(s), ${formatPence(res.marked_pence)}`);
            }
            onChanged();
        } catch (e) {
            toast.error((e as Error).message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-w-0 rounded-xl border border-danger/20 bg-tint/[0.02]">
            <div className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-fg">{row.display_name}</p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                            <span className="font-mono text-xs text-fg-4">{row.code}</span>
                            <button onClick={copyLink} aria-label={`Copy referral link for ${row.code}`}
                                className="inline-flex items-center gap-1 text-xs text-danger/70 hover:text-danger">
                                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                {copied ? 'Copied' : 'Link'}
                            </button>
                        </div>
                    </div>
                    <span className={`shrink-0 rounded border px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_CLS[status]}`}>
                        {status}
                    </span>
                </div>

                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                    <div className="min-w-0">
                        <dt className="text-[10px] uppercase tracking-wider text-fg-4">Referred / converted</dt>
                        <dd className="font-mono text-fg-2">{row.referred_orgs} / {row.converted_orgs}</dd>
                    </div>
                    <div className="min-w-0">
                        <dt className="text-[10px] uppercase tracking-wider text-fg-4">Paying this month</dt>
                        <dd className="font-mono text-fg-2">{row.paying_orgs_period}</dd>
                    </div>
                    <div className="min-w-0">
                        <dt className="text-[10px] uppercase tracking-wider text-fg-4">{monthLabel(period)} net</dt>
                        <dd className="font-mono text-fg-2">{formatPence(row.period_net_pence)}</dd>
                    </div>
                    <div className="min-w-0">
                        <dt className="text-[10px] uppercase tracking-wider text-fg-4">Outstanding</dt>
                        <dd className={`font-mono ${dueNow ? 'text-success' : 'text-fg-2'}`}>
                            {formatPence(outstanding)}
                        </dd>
                    </div>
                </dl>

                {exclusionInert && (
                    <p className="mt-4 flex items-start gap-2 rounded border border-gold/30 bg-gold-fill/5 px-3 py-2 text-xs text-gold/90">
                        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span className="min-w-0">
                            No contact email — the self-referral AND reciprocal-referral
                            exclusions are both off for this partner. They would earn commission
                            on their own subscription, and could swap referrals with another
                            partner unchecked. Add it below.
                        </span>
                    </p>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button onClick={toggle} className={btnCls}>
                        <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                        {row.referred_orgs} referred
                    </button>
                    {dueNow && (
                        <button onClick={markPaid} disabled={busy} className={`${btnCls} border-success/30 text-success hover:bg-success/10`}>
                            <BanknoteArrowUp className="h-3 w-3" /> Mark paid
                        </button>
                    )}
                    {status === 'active' && (
                        <button onClick={() => setStatus('paused')} disabled={busy} className={btnCls}>Pause</button>
                    )}
                    {status === 'paused' && (
                        <button onClick={() => setStatus('active')} disabled={busy} className={btnCls}>Resume</button>
                    )}
                    {status !== 'terminated' && (
                        <button onClick={() => setStatus('terminated')} disabled={busy} className={btnCls}>Terminate</button>
                    )}
                </div>
            </div>

            {expanded && (
                <div className="space-y-5 border-t border-danger/10 px-4 pb-5 pt-4">
                    {/* Rates */}
                    <div>
                        <p className="mb-2 text-[10px] uppercase tracking-widest text-fg-4">Commission</p>
                        {editingRates ? (
                            <div className="flex flex-wrap items-end gap-3">
                                <div className="min-w-0">
                                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-fg-4">Initial %</label>
                                    <input className={`${inputCls} w-24`} type="number" min="0" max="100" step="0.5"
                                        value={initialPct} onChange={e => setInitialPct(e.target.value)} />
                                </div>
                                <div className="min-w-0">
                                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-fg-4">Tail %</label>
                                    <input className={`${inputCls} w-24`} type="number" min="0" max="100" step="0.5"
                                        value={tailPct} onChange={e => setTailPct(e.target.value)} />
                                </div>
                                <button onClick={saveRates} disabled={busy} className={btnCls}>Save</button>
                                <button onClick={() => setEditingRates(false)} className={btnCls}>Cancel</button>
                            </div>
                        ) : (
                            <div className="flex flex-wrap items-center gap-3">
                                <p className="font-mono text-sm text-fg-2">
                                    {pct(record?.rate_bps_initial ?? 3000)} for {record?.initial_term_months ?? 12} months,
                                    then {pct(record?.rate_bps_tail ?? 1500)}
                                </p>
                                <button onClick={beginEditRates} className={btnCls}>Edit rates</button>
                            </div>
                        )}
                    </div>

                    {/* Contact */}
                    <div>
                        <p className="mb-2 text-[10px] uppercase tracking-widest text-fg-4">Contact email</p>
                        {editingEmail ? (
                            <div className="flex flex-wrap items-end gap-3">
                                <div className="min-w-0 flex-1 sm:max-w-xs">
                                    <input
                                        className={inputCls}
                                        type="email"
                                        inputMode="email"
                                        autoComplete="off"
                                        placeholder="name@example.com"
                                        aria-label={`Contact email for ${row.display_name}`}
                                        value={emailDraft}
                                        onChange={e => setEmailDraft(e.target.value)}
                                    />
                                </div>
                                <button onClick={saveEmail} disabled={busy} className={btnCls}>Save</button>
                                <button onClick={() => setEditingEmail(false)} className={btnCls}>Cancel</button>
                            </div>
                        ) : (
                            <div className="flex flex-wrap items-center gap-3">
                                <p className="min-w-0 break-all font-mono text-sm text-fg-2">
                                    {contactEmail ?? <span className="text-gold/80">Not set</span>}
                                </p>
                                <button onClick={beginEditEmail} className={btnCls}>
                                    <Mail className="h-3 w-3" /> {contactEmail ? 'Edit' : 'Add'} email
                                </button>
                            </div>
                        )}
                        <p className="mt-2 text-xs leading-relaxed text-fg-4">
                            Used to enforce the self-referral exclusion — a signup whose account email
                            matches this address is not attributed to {row.display_name}. It is also the
                            address the payout goes to. Not shown to customers.
                        </p>
                    </div>

                    {/* Referred organisations */}
                    <div>
                        <p className="mb-2 text-[10px] uppercase tracking-widest text-fg-4">Referred organisations</p>
                        {referrals === null ? (
                            <Loader2 className="h-4 w-4 animate-spin text-danger" />
                        ) : referrals.length === 0 ? (
                            <p className="text-sm text-fg-4">Nobody yet.</p>
                        ) : (
                            <div className="min-w-0 overflow-x-auto">
                                <table className="w-full min-w-[560px] text-sm">
                                    <thead>
                                        <tr className="border-b border-danger/20 text-left text-[10px] uppercase tracking-widest text-fg-4">
                                            <th className="py-2 pr-4 font-medium">Organisation</th>
                                            <th className="py-2 pr-4 font-medium">Signed up</th>
                                            <th className="py-2 pr-4 font-medium">Converted</th>
                                            <th className="py-2 pr-4 font-medium">Tier</th>
                                            <th className="py-2 pr-4 font-medium">Via</th>
                                            <th className="py-2 text-right font-medium">Earned</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {referrals.map(r => (
                                            <tr key={r.org_id} className="border-b border-danger/10 last:border-0">
                                                <td className="py-2 pr-4 text-fg-2">{r.org_name}</td>
                                                <td className="py-2 pr-4 font-mono text-xs text-fg-4">
                                                    {new Date(r.first_seen_at).toLocaleDateString('en-GB')}
                                                </td>
                                                <td className="py-2 pr-4 font-mono text-xs text-fg-4">
                                                    {r.converted_at ? new Date(r.converted_at).toLocaleDateString('en-GB') : '—'}
                                                </td>
                                                <td className="py-2 pr-4 font-mono text-xs text-fg-3">{r.subscription_tier}</td>
                                                <td className="py-2 pr-4 font-mono text-xs text-fg-4">
                                                    {r.source === 'ref_link' ? 'link' : 'code'}
                                                </td>
                                                <td className="py-2 text-right font-mono text-fg-2">
                                                    {formatPence(r.lifetime_pence)}
                                                    {Number(r.clawed_back_pence) > 0 && (
                                                        <span className="ml-1 text-xs text-gold/70">
                                                            (−{formatPence(r.clawed_back_pence)})
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Panel ──────────────────────────────────────────────────────────────────

export const PartnersPanel = () => {
    const [rows, setRows] = useState<PartnerOverviewRow[]>([]);
    const [records, setRecords] = useState<Record<string, PartnerRecord>>({});
    const [period, setPeriod] = useState(periodKey());
    const [reloadKey, setReloadKey] = useState(0);

    // `loading` is DERIVED, not stored: we are loading whenever the period on
    // screen is not the one we last finished fetching. That keeps every
    // setState below behind an `await` (no synchronous setState in an effect,
    // which the lint ratchet rejects) and removes a whole state variable that
    // could disagree with the data beside it.
    const [loadedKey, setLoadedKey] = useState<string | null>(null);
    const currentKey = `${period}#${reloadKey}`;
    const loading = loadedKey !== currentKey;

    const reload = useCallback(() => setReloadKey(k => k + 1), []);

    useEffect(() => {
        // Guards against a slow response for an old month landing after a fast
        // one for a new month and overwriting it — easy to trigger here, since
        // the month arrows let you click through periods faster than the RPC
        // returns.
        let cancelled = false;
        (async () => {
            try {
                const [overview, partnerList] = await Promise.all([
                    listPartnerOverview(period),
                    listPartners(),
                ]);
                if (!cancelled) {
                    setRows(overview);
                    setRecords(Object.fromEntries(partnerList.map(p => [p.id, p])));
                }
            } catch (e) {
                if (!cancelled) {
                    toast.error(`Load failed: ${(e as Error).message}`);
                    setRows([]);
                }
            } finally {
                if (!cancelled) setLoadedKey(`${period}#${reloadKey}`);
            }
        })();
        return () => { cancelled = true; };
    }, [period, reloadKey]);

    const exportCsv = () => {
        if (!rows.length) return toast.error('Nothing to export');
        const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `forgedash-partners-${period}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Display-only totals over rows the server already summed. No rate is
    // applied and no commission is derived here.
    const totalOutstanding = rows.reduce((sum, r) => sum + Number(r.outstanding_pence), 0);
    const dueNow = rows.filter(r => Number(r.outstanding_pence) >= MIN_PAYOUT_PENCE);

    return (
        <div className="min-h-screen bg-sunken text-fg">

            <header className="sticky top-0 z-50 border-b border-danger/20 bg-scrim/50 backdrop-blur-sm">
                <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="shrink-0 rounded-lg border border-danger/20 bg-danger/10 p-2">
                            <Handshake className="h-5 w-5 text-danger" />
                        </div>
                        <div className="min-w-0">
                            <h1 className="truncate text-lg font-bold uppercase tracking-widest text-danger">Partners</h1>
                            <p className="truncate text-[10px] uppercase tracking-[0.3em] text-danger/40">Referrals &amp; commission</p>
                        </div>
                    </div>
                    <Link to="/" className="flex shrink-0 items-center gap-2 rounded border border-danger/20 px-4 py-2 text-xs uppercase tracking-widest text-danger transition-colors hover:bg-danger/10">
                        <ArrowLeft className="h-3 w-3" /> Cockpit
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-6xl space-y-6 px-4 py-8 sm:px-6">
                <AddPartnerForm onCreated={reload} />

                {/* Period selector + export */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setPeriod(p => shiftMonth(p, -1))} aria-label="Previous month"
                            className="grid h-11 w-11 place-items-center rounded border border-danger/20 text-danger transition-colors hover:bg-danger/10">
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span className="min-w-[9rem] text-center font-mono text-sm uppercase tracking-widest text-fg-2">
                            {monthLabel(period)}
                        </span>
                        <button onClick={() => setPeriod(p => shiftMonth(p, 1))} aria-label="Next month"
                            disabled={period >= periodKey()}
                            className="grid h-11 w-11 place-items-center rounded border border-danger/20 text-danger transition-colors hover:bg-danger/10 disabled:opacity-30">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                    <button onClick={exportCsv} className={btnCls}>
                        <Download className="h-3 w-3" /> Export CSV
                    </button>
                </div>

                {/* Payout summary */}
                <section className="grid gap-3 sm:grid-cols-3">
                    <div className="min-w-0 rounded-xl border border-danger/20 bg-tint/[0.02] p-4">
                        <p className="text-[10px] uppercase tracking-widest text-fg-4">Partners</p>
                        <p className="mt-1 font-mono text-2xl text-fg">{rows.length}</p>
                    </div>
                    <div className="min-w-0 rounded-xl border border-danger/20 bg-tint/[0.02] p-4">
                        <p className="text-[10px] uppercase tracking-widest text-fg-4">Outstanding</p>
                        <p className="mt-1 font-mono text-2xl text-fg">{formatPence(totalOutstanding)}</p>
                    </div>
                    <div className="min-w-0 rounded-xl border border-danger/20 bg-tint/[0.02] p-4">
                        <p className="text-[10px] uppercase tracking-widest text-fg-4">Over £50 threshold</p>
                        <p className="mt-1 font-mono text-2xl text-fg">{dueNow.length}</p>
                    </div>
                </section>

                {loading ? (
                    <div className="flex justify-center py-16">
                        <Loader2 className="h-6 w-6 animate-spin text-danger" />
                    </div>
                ) : rows.length === 0 ? (
                    <p className="rounded-xl border border-danger/20 bg-tint/[0.02] p-8 text-center text-sm text-fg-4">
                        No partners yet. Add one above, then create a matching Stripe promotion code.
                    </p>
                ) : (
                    <div className="space-y-3">
                        {rows.map(r => (
                            <PartnerCard
                                key={r.partner_id}
                                row={r}
                                record={records[r.partner_id]}
                                period={period}
                                onChanged={reload}
                            />
                        ))}
                    </div>
                )}

                <p className="text-xs leading-relaxed text-fg-4">
                    Green outstanding balances have cleared the £50 payout threshold; commission becomes
                    payable 30 days after it accrues. Rates apply to revenue received excluding VAT and net
                    of discount. Clawbacks net off the period they are recorded in, so a period figure can
                    legitimately be negative. <strong className="text-fg-4">Mark paid after the bank
                    transfer, not before</strong> — it is a record, not an instruction.
                </p>
            </main>
        </div>
    );
};
